import type { DashboardRankedItem, DashboardSummary } from '@brechoflow/contracts';
import { BarChart } from '@/features/finance/components/charts';
import { formatCurrency } from '@/lib/format';
import { formatInt, formatRate } from './kpi';
import { BarRow, InlineEmpty, Panel } from './parts';

const dayLabel = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const max = (xs: number[]) => Math.max(1, ...xs);

function Ranking({ items, empty, value }: { items: DashboardRankedItem[]; empty: string; value: (i: DashboardRankedItem) => number }) {
  if (items.length === 0) return <InlineEmpty>{empty}</InlineEmpty>;
  const top = max(items.map(value));
  return (
    <div className="flex flex-col">
      {items.map((i) => (
        <BarRow key={`${i.id}-${i.name}`} label={i.name} valueText={formatCurrency(value(i))} detail={`${formatInt(i.qty)} un.`} scale={value(i) / top} title={`${i.name}: ${formatCurrency(i.revenueCents)} vendidos, lucro ${formatCurrency(i.profitCents)}`} />
      ))}
    </div>
  );
}

/** Barras simples de contagem (evolução do estoque); o eixo parte do zero e há tabela para leitores de tela. */
function CountBars({ points, label }: { points: { date: string; available: number }[]; label: string }) {
  const top = max(points.map((p) => p.available));
  const step = Math.max(1, Math.ceil(points.length / 8));
  const flat = points.every((p) => p.available === 0);
  return (
    <figure className="m-0" data-testid="chart-stock">
      <div role="img" aria-label={label} className="flex h-40 items-end gap-px border-b">
        {points.map((p) => (
          <div key={p.date} tabIndex={0} title={`${dayLabel(p.date)}: ${formatInt(p.available)} peças disponíveis`} aria-label={`${dayLabel(p.date)}: ${formatInt(p.available)} peças disponíveis`} className="flex h-full min-w-0 flex-1 items-end outline-none hover:bg-muted/40 focus-visible:bg-muted/60">
            <span className="w-full rounded-t-[3px] bg-viz-bar" style={{ height: `${p.available > 0 ? Math.max((p.available / top) * 100, 2) : 0}%` }} />
          </div>
        ))}
      </div>
      <div className="flex pt-1.5" aria-hidden>
        {points.map((p, i) => (
          <div key={p.date} className="relative h-4 min-w-0 flex-1">
            {i % step === 0 && <span className="absolute left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap text-muted-foreground">{dayLabel(p.date)}</span>}
          </div>
        ))}
      </div>
      {flat && <p className="mt-1 text-sm text-muted-foreground">Nenhuma peça disponível no período.</p>}
      {/* o sr-only vai no div: tabela ignora height/overflow e, absoluta, esticava a rolagem da página */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <tbody>
            {points.map((p) => (
              <tr key={p.date}>
                <td>{p.date}</td>
                <td>{p.available}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/** Painéis e gráficos do dashboard (dados reais; cada bloco só aparece se o servidor o devolveu). */
export function DashboardPanels({ data }: { data: DashboardSummary }) {
  const { orders, sales, charts, stock } = data;
  if (!orders && !sales && !charts && !stock) return null;
  const statusTop = max((orders?.byStatus ?? []).map((s) => s.count));
  const categories = charts?.salesByCategory ?? [];
  const catTop = max(categories.map((c) => c.revenueCents));
  return (
    <div className="mt-5 grid gap-5 lg:grid-cols-2" data-testid="dashboard-panels">
      {charts && (
        <Panel id="p-revenue" title="Faturamento no período" subtitle="Valor das peças vendidas por dia" className="lg:col-span-2" testId="panel-revenue">
          <BarChart
            labels={charts.revenueSeries.map((d) => dayLabel(d.date))}
            series={[{ name: 'Faturamento', className: 'bg-viz-received', values: charts.revenueSeries.map((d) => d.revenueCents) }]}
            ariaLabel="Faturamento por dia no período"
            testId="chart-revenue"
          />
        </Panel>
      )}
      {orders && (
        <Panel id="p-status" title="Pedidos por status" subtitle="Pedidos criados no período" testId="panel-status">
          {orders.byStatus.every((s) => s.count === 0) ? (
            <InlineEmpty>Nenhum pedido neste período.</InlineEmpty>
          ) : (
            <div className="flex flex-col">
              {orders.byStatus
                .filter((s) => s.count > 0)
                .map((s) => (
                  <BarRow key={s.status} label={s.label} valueText={formatInt(s.count)} scale={s.count / statusTop} title={`${s.label}: ${formatInt(s.count)} pedidos`} />
                ))}
            </div>
          )}
        </Panel>
      )}
      {charts && (
        <Panel id="p-cat" title="Vendas por categoria" subtitle="Faturamento no período" testId="panel-categories">
          {categories.length === 0 ? (
            <InlineEmpty>Nenhuma venda paga neste período.</InlineEmpty>
          ) : (
            <div className="flex flex-col">
              {categories.map((c) => (
                <BarRow key={c.name} label={c.name} valueText={formatCurrency(c.revenueCents)} detail={`${formatInt(c.qty)} un.`} scale={c.revenueCents / catTop} title={`${c.name}: ${formatCurrency(c.revenueCents)} em ${formatInt(c.qty)} peças`} />
              ))}
            </div>
          )}
        </Panel>
      )}
      {sales && (
        <Panel id="p-top" title="Mais vendidos" subtitle="Por faturamento no período" testId="panel-top">
          <Ranking items={sales.topProducts} empty="Nenhuma venda paga neste período." value={(i) => i.revenueCents} />
        </Panel>
      )}
      {sales && (
        <Panel id="p-margin" title="Produtos com maior margem" subtitle="Peça consignada: lucro é a comissão" testId="panel-margin">
          <Ranking items={sales.topMargin} empty="Nenhuma venda paga neste período." value={(i) => i.profitCents} />
        </Panel>
      )}
      {sales && (
        <Panel id="p-margins" title="Margem" subtitle="Estimada no estoque e realizada nas vendas do período" testId="panel-margins">
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-[0.8125rem] text-muted-foreground">Margem estimada (estoque)</dt>
              <dd className="mt-1 font-display text-xl font-semibold tabular" data-testid="margin-estimated">
                {formatCurrency(sales.estimatedMarginCents)}
              </dd>
            </div>
            <div>
              <dt className="text-[0.8125rem] text-muted-foreground">Margem realizada</dt>
              <dd className="mt-1 font-display text-xl font-semibold tabular" data-testid="margin-realized">
                {formatCurrency(sales.realizedMarginCents)}
                <span className="ml-2 text-xs font-normal text-muted-foreground">{formatRate(sales.realizedMarginRate)}</span>
              </dd>
            </div>
          </dl>
        </Panel>
      )}
      {charts && stock && (
        <Panel id="p-stock" title="Evolução do estoque" subtitle="Peças disponíveis por dia" testId="panel-stock-evolution">
          <CountBars points={charts.stockEvolution} label="Peças disponíveis por dia no período" />
        </Panel>
      )}
    </div>
  );
}
